import { Link, useParams } from 'react-router-dom';
import { newsList } from '@/mocks/news';
import { episodes } from '@/mocks/episodes';
import Breadcrumb from '@/components/base/Breadcrumb';
import EmptyState from '@/components/base/EmptyState';
import usePageMeta from '@/hooks/usePageMeta';

export default function NewsDetail() {
  const { id } = useParams<{ id: string }>();
  const news = newsList.find((item) => String(item.id) === id);

  usePageMeta({
    title: news ? `${news.title} - 女友的妈妈影视资讯` : '资讯详情 - 女友的妈妈',
    description: news ? news.summary : '《女友的妈妈》影视资讯详情页',
    keywords: '女友的妈妈影视资讯,影视新闻详情',
  });

  if (!news) {
    return (
      <div className="w-full px-4 md:px-8 py-16">
        <div className="max-w-[800px] mx-auto">
          <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '影视资讯', to: '/news' }, { label: '资讯详情' }]} />
          <div className="mt-8">
            <EmptyState
              icon="ri-file-warning-line"
              title="该资讯内容不存在"
              description="你访问的资讯可能已下线或链接有误，返回资讯列表看看其他内容吧。"
            />
            <div className="mt-6 text-center">
              <Link to="/news" className="text-sm text-primary-600 hover:text-accent-600 transition-colors">
                返回影视资讯
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const relatedNews = newsList.filter((item) => item.id !== news.id).slice(0, 3);
  const relatedEpisodes = episodes.filter((episode) => news.episodeIds.includes(episode.id));

  return (
    <>
      <section className="w-full bg-background-100 border-b border-background-200">
        <div className="max-w-[860px] mx-auto px-4 md:px-8 py-6 md:py-8">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: '影视资讯', to: '/news' },
              { label: '资讯详情' },
            ]}
          />
        </div>
      </section>

      <article className="w-full px-4 md:px-8 py-8 md:py-10">
        <div className="max-w-[860px] mx-auto">
          <header className="mb-6">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-secondary-100 text-secondary-900 text-xs font-medium">
              <i className="ri-price-tag-3-line"></i>
              {news.category}
            </span>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 leading-snug mt-3">
              {news.title}
            </h1>
            <p className="flex items-center gap-2 text-xs md:text-sm text-foreground-500 mt-3">
              <i className="ri-time-line"></i>
              发布时间：{news.date}
            </p>
          </header>

          <div className="relative w-full aspect-[16/9] rounded-lg overflow-hidden bg-background-200">
            <img
              src={news.cover}
              alt={`女友的妈妈 影视资讯 ${news.title} 配图`}
              title={`${news.title} 女友的妈妈 影视资讯`}
              className="w-full h-full object-cover object-top"
            />
          </div>

          <div className="mt-8 space-y-5">
            {news.content.map((paragraph, index) => (
              <p key={index} className="text-sm md:text-base leading-loose text-foreground-700">
                {paragraph}
              </p>
            ))}
          </div>

          {relatedEpisodes.length > 0 && (
            <section className="mt-10 rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="flex items-center gap-2 text-base font-bold text-foreground-950 mb-3">
                <i className="ri-links-line text-primary-600"></i>
                相关剧集
              </h2>
              <div className="flex flex-wrap gap-2.5">
                {relatedEpisodes.map((episode) => (
                  <Link
                    key={episode.id}
                    to={`/episode/${episode.id}`}
                    className="px-4 py-2 rounded-full border border-background-300 bg-background-50 text-sm text-foreground-800 hover:border-primary-400 hover:text-primary-600 transition-colors whitespace-nowrap"
                  >
                    第{String(episode.number).padStart(2, '0')}集 · {episode.title}
                  </Link>
                ))}
              </div>
            </section>
          )}

          <div className="mt-6">
            <Link
              to="/news"
              className="inline-flex items-center gap-1.5 text-sm text-primary-600 hover:text-accent-600 transition-colors"
            >
              <i className="ri-arrow-left-line"></i>
              返回影视资讯
            </Link>
          </div>

          <section className="mt-12">
            <h2 className="flex items-center gap-2 text-lg font-bold text-foreground-950 mb-5">
              <i className="ri-newspaper-line text-primary-600"></i>
              相关新闻
            </h2>
            <div className="space-y-3">
              {relatedNews.map((item) => (
                <Link
                  key={item.id}
                  to={`/news/${item.id}`}
                  className="group flex items-start gap-3 rounded-lg border border-background-200 bg-background-50 p-4 hover:border-primary-300 transition-colors"
                >
                  <i className="ri-article-line text-accent-500 mt-0.5"></i>
                  <span className="min-w-0">
                    <span className="block text-sm md:text-base font-medium text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2">
                      {item.title}
                    </span>
                    <span className="block text-xs text-foreground-400 mt-1">{item.date}</span>
                  </span>
                </Link>
              ))}
            </div>
          </section>
        </div>
      </article>
    </>
  );
}